import React from 'react';
import FoldableCardBlock from '../FoldableCardBlock';
import TEST_ID from './index.testid';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { safeFormattedDateTime } from '~/util/date';
import { Body, Heading5 } from '~/components/atom/Typography';
import Divider from '~/components/atom/Divider';
import Button from '~/components/atom/Button';
import scrollElementIntoView from '~/util/scrollElementIntoView';
import useSessionHydration from '~/hooks/useSessionHydration';
import type { GetContactQuery } from '~/graphql/types';

type Props = {
  contactId: string;
  lfaRequests: NonNullable<
    NonNullable<GetContactQuery['getContact']>['LFA_Requests']
  >;
};

const text = {
  title: 'LFA Requests',
  noRequests: 'Geen LFA requests gevonden',
  status: 'Status',
  createdDate: 'Aangemaakt',
  closedDate: 'Gesloten',
  note: 'Notitie',
  viewEvent: 'Bekijk gebeurtenis',
};

const ContactLfaRequestsBlock: React.FC<Props> = ({
  contactId: _contactId,
  lfaRequests,
}) => {
  const [{ impersonatedBy }] = useSessionHydration();
  const isImpersonating = !!impersonatedBy;
  if (!isImpersonating) return null;

  if (!lfaRequests || lfaRequests.length === 0) {
    return (
      <FoldableCardBlock
        icon="user"
        title={text.title}
        dataTestId={TEST_ID.CONTAINER}
      >
        <JustificationContainer
          width="100%"
          direction="column"
          gap="m"
          padding={['xxs', null, null, null]}
        >
          <Body size="base" fontWeight="regular">
            {text.noRequests}
          </Body>
        </JustificationContainer>
      </FoldableCardBlock>
    );
  }

  return (
    <FoldableCardBlock
      icon="user"
      title={text.title}
      dataTestId={TEST_ID.CONTAINER}
    >
      <JustificationContainer
        width="100%"
        direction="column"
        gap="m"
        padding={['xxs', null, null, null]}
      >
        {lfaRequests.map((request, index) => (
          <React.Fragment key={request.id}>
            {index > 0 && <Divider margin={['m', null]} />}
            <JustificationContainer width="100%" direction="column" gap="xs">
              <JustificationContainer
                width="100%"
                justification="space-between"
                align="center"
              >
                <Heading5 fontWeight="medium" margin={[null]}>
                  {text.status}: {request.status}
                </Heading5>
              </JustificationContainer>
              <Body
                size="s"
                fontWeight="regular"
                color={{ group: 'secondary' }}
              >
                {text.createdDate}:{' '}
                {safeFormattedDateTime(new Date(request.createdDate))}
              </Body>
              {request.closedDate && (
                <Body
                  size="s"
                  fontWeight="regular"
                  color={{ group: 'secondary' }}
                >
                  {text.closedDate}:{' '}
                  {safeFormattedDateTime(new Date(request.closedDate))}
                </Body>
              )}
              {request.note && (
                <Body size="base" fontWeight="regular" margin={['xs', null]}>
                  {text.note}: {request.note}
                </Body>
              )}
              {request.eventId && (
                <Button
                  ghost
                  label={text.viewEvent}
                  onClick={async () => {
                    await scrollElementIntoView(request.eventId);
                  }}
                />
              )}
            </JustificationContainer>
          </React.Fragment>
        ))}
      </JustificationContainer>
    </FoldableCardBlock>
  );
};

export default ContactLfaRequestsBlock;
